/**
 * Shared pattern category constants and utilities
 * Used by GBrainPage, IntelligencePage, and their respective sidebars
 */

/**
 * Category colours, from the --category-* scale in index.css.
 *
 * A categorical scale, not a status one: `reliability` being red means "the
 * reliability category", never "failing". That distinction is why these cannot
 * collapse into success/warning/error.
 */
export const CATEGORY_COLORS: Record<string, string> = {
  architecture: 'bg-category-1',
  performance: 'bg-category-3',
  reliability: 'bg-category-5',
  security: 'bg-category-2',
  scalability: 'bg-category-4',
  maintainability: 'bg-category-6',
};

/**
 * The same six hues as literal sRGB, for the 3D explorers.
 *
 * These stay hardcoded ON PURPOSE: THREE.Color needs a concrete colour and cannot
 * resolve a CSS custom property, so there is no token form available to WebGL. They
 * mirror --category-* and must be updated alongside it; `pattern-categories.test.ts`
 * asserts the two records at least describe the same category set, which catches a
 * key added to one and not the other. It cannot verify the hues match — that part is
 * a genuine manual duplication, recorded rather than hidden.
 */
export const CATEGORY_HEX_COLORS: Record<string, string> = {
  architecture: '#3b82f6',
  performance: '#f59e0b',
  reliability: '#ef4444',
  security: '#a855f7',
  scalability: '#22c55e',
  maintainability: '#06b6d4',
};

/** Dot variant — same hues at reduced alpha rather than a second, lighter scale. */
export const CATEGORY_DOT_COLORS: Record<string, string> = {
  architecture: 'bg-category-1/80',
  performance: 'bg-category-3/80',
  reliability: 'bg-category-5/80',
  security: 'bg-category-2/80',
  scalability: 'bg-category-4/80',
  maintainability: 'bg-category-6/80',
};

/**
 * Format a category name for display
 * @param category - The category identifier (e.g., 'architecture')
 * @returns Human-readable label (e.g., 'Architecture')
 */
export function formatCategoryLabel(category: string): string {
  return category
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Format a timestamp as relative time
 * @param timestamp - ISO timestamp or Date object
 * @returns Relative time string (e.g., '2 hours ago', 'just now')
 */
export function formatTimeAgo(timestamp: string | Date): string {
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
}
